import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type {
  DispensationEvidenceListModel,
  DispensationEvidenceModel,
  DispensationQueryParams,
} from "@/features/cle-compliance/models/dispensation";
import { get, getBlob } from "./util";

export const DISPENSATIONS_QUERY_KEY = ["dispensations"] as const;

export const DISPENSATIONS_LIST_QUERY_KEY = [
  ...DISPENSATIONS_QUERY_KEY,
  "list",
] as const;

export const createDispensationDetailQueryKey = (id: string) =>
  [...DISPENSATIONS_QUERY_KEY, "detail", id] as const;

export const createDispensationFileQueryKey = (id: string) =>
  [...DISPENSATIONS_QUERY_KEY, "files", id] as const;

const DISPENSATIONS_QUERY = "documents/dispensations";

const createDispensationDownloadQuery = (id: string): string =>
  `${DISPENSATIONS_QUERY}/${id}/download`;

const createDispensationsForReportingQuery = (periodId: string): string =>
  `${DISPENSATIONS_QUERY}/available-for-period/${periodId}`;

const createSearchParams = ({
  page,
  page_size,
  reported,
}: DispensationQueryParams): string => {
  const searchParams = new URLSearchParams();

  if (reported != null) {
    searchParams.set("reported", String(reported));
  }

  if (page != null) {
    searchParams.set("page", String(page));
  }

  if (page_size != null) {
    searchParams.set("page_size", String(page_size));
  }

  return searchParams.toString();
};

const createListQuery = (params: DispensationQueryParams = {}): string => {
  const queryString = createSearchParams(params);

  return queryString
    ? `${DISPENSATIONS_QUERY}?${queryString}`
    : DISPENSATIONS_QUERY;
};

export const fetchDispensations = (
  params: DispensationQueryParams = {},
): Promise<PaginatedResponse<DispensationEvidenceListModel>> =>
  get<PaginatedResponse<DispensationEvidenceListModel>>({
    query: createListQuery(params),
  });

export const fetchDispensationsForReporting = (
  periodId: string,
): Promise<DispensationEvidenceListModel[]> =>
  get<DispensationEvidenceListModel[]>({
    query: createDispensationsForReportingQuery(periodId),
  });

export const fetchDispensationById = (
  id: string,
): Promise<DispensationEvidenceModel> =>
  get<DispensationEvidenceModel>({
    query: `${DISPENSATIONS_QUERY}/${id}`,
  });

export const fetchDispensationDocumentFile = (id: string): Promise<Blob> =>
  getBlob({
    query: createDispensationDownloadQuery(id),
  });
